import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferOutput } from "valibot";
import { boolean, fallback, nullable, object, objectAsync } from "valibot";
import { integerValue, label, requireResource } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import CustomerResourceInput from "../CustomerResourceInput";
import { DECORATIVE_IMAGE_ALT, MAX_IMAGE_HEIGHT, MAX_IMAGE_WIDTH } from "../../constants";
import { NumberInput } from "../../components/inputs/NumberInput";
import { requireResourceAsync } from "../../utils/validation/async";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { Show } from "solid-js";
import { PreviewContentLabel } from "../FieldPreviewComponents";
import { RemoteImage } from "../../components/image/Image";
import { isNil } from "lodash-es";
import { keyFromSelector } from "i18next";

const ResourceSchema = objectAsync({
  resource_id: requireResourceAsync(),
});

const Schema = object({
  label_text: fallback(label(), ""),
  resource_id: requireResource(),
  max_width: nullable(integerValue(1, MAX_IMAGE_WIDTH)),
  max_height: nullable(integerValue(1, MAX_IMAGE_HEIGHT)),
  click_to_view: fallback(boolean(), false),
}) satisfies GenericSchema<InferOutput<typeof ResourceSchema>>;

export type ImageFieldProperties = InferOutput<typeof Schema>;

const ImageField: Field<ImageFieldProperties, DefaultDtoType> = {
  type: "image",
  category: "layout",
  widget: "com.moreapps:image:1",
  schema: Schema,
  resourceSchema: ResourceSchema,
  hasData: false,
  label: "default",
  defaultProperties: () => ({
    click_to_view: false,
    label_text: "",
    resource_id: undefined,
    max_width: null,
    max_height: null,
  }),
  icon: "vector",
  titleKey: keyFromSelector(($) => $.IMAGE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.IMAGE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_IMAGE_LABEL_TEXT),
    resource_id: keyFromSelector(($) => $.FIELD_PROPERTY_RESOURCE_ID),
    max_width: keyFromSelector(($) => $.FIELD_PROPERTY_MAX_WIDTH),
    max_height: keyFromSelector(($) => $.FIELD_PROPERTY_MAX_HEIGHT),
    click_to_view: keyFromSelector(($) => $.FIELD_PROPERTY_CLICK_TO_VIEW),
  },
  PreviewContent: (props) => {
    const image = (
      <RemoteImage
        size="custom"
        resourceId={props.properties.resource_id}
        alt={DECORATIVE_IMAGE_ALT}
        style={{
          "max-width": !isNil(props.properties.max_width) ? `${props.properties.max_width}px` : undefined,
          "max-height": !isNil(props.properties.max_height) ? `${props.properties.max_height}px` : undefined,
        }}
        cover={false}
      />
    );

    return (
      <Show when={props.properties.label_text} fallback={image}>
        <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={false}>
          {image}
        </PreviewContentLabel>
      </Show>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_IMAGE)}>
          <CustomerResourceInput
            label={t(props.propertyKeys.resource_id)}
            onClear={() => props.setValue("resource_id", null)}
            value={props.data.properties.resource_id}
            onSelect={(file) => props.setValue("resource_id", file.id)}
            errorMessage={props.getError("resource_id")}
            required
          />
          <NumberInput
            label={t(props.propertyKeys.max_width)}
            value={props.data.properties.max_width}
            minValue={0}
            maxValue={MAX_IMAGE_WIDTH}
            onBlur={(value) => props.setValue("max_width", value)}
            errorMessage={props.getError("max_width")}
          />
          <NumberInput
            label={t(props.propertyKeys.max_height)}
            value={props.data.properties.max_height}
            minValue={0}
            maxValue={MAX_IMAGE_HEIGHT}
            onBlur={(value) => props.setValue("max_height", value)}
            errorMessage={props.getError("max_height")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(props.propertyKeys.click_to_view)}
            checked={props.data.properties.click_to_view}
            setChecked={(value) => props.setValue("click_to_view", value)}
            description={t(($) => $.FIELD_PROPERTY_CLICK_TO_VIEW_DESCRIPTION)}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(ImageField);
